/**
 * User Geolocation Module
 * Detects user country using IP geolocation and caches in localStorage
 * Non-invasive: Doesn't modify native Shopify objects
 * 
 * localStorage structure (single key):
 *   'user_geo': {
 *     country: { code: 'US', name: 'United States', city: '...', ... },
 *     timestamp: 1698518400000
 *   }
 * 
 * Usage:
 *   const country = await window.UserGeo.getCountry();
 *   console.log(country); // { code: 'US', name: 'United States' }
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'ugeo'; // Single key with nested JSON structure
  const CACHE_DURATION = 1 * 60 * 60 * 1000; // 1 hour in milliseconds
  const IPINFO_TOKEN = 'a5fbce6372f0e1';
  const DEFAULT_COUNTRY = 'US';

// ISO 3166-1 alpha-2 country/territory names (common English forms)
// Includes widely used non-ISO code XK (Kosovo) at the end.
const COUNTRY_NAMES = {
  'AF': 'Afghanistan',
  'AX': 'Åland Islands',
  'AL': 'Albania',
  'DZ': 'Algeria',
  'AS': 'American Samoa',
  'AD': 'Andorra',
  'AO': 'Angola',
  'AI': 'Anguilla',
  'AQ': 'Antarctica',
  'AG': 'Antigua and Barbuda',
  'AR': 'Argentina',
  'AM': 'Armenia',
  'AW': 'Aruba',
  'AU': 'Australia',
  'AT': 'Austria',
  'AZ': 'Azerbaijan',
  'BS': 'Bahamas',
  'BH': 'Bahrain',
  'BD': 'Bangladesh',
  'BB': 'Barbados',
  'BY': 'Belarus',
  'BE': 'Belgium',
  'BZ': 'Belize',
  'BJ': 'Benin',
  'BM': 'Bermuda',
  'BT': 'Bhutan',
  'BO': 'Bolivia',
  'BQ': 'Bonaire, Sint Eustatius and Saba',
  'BA': 'Bosnia and Herzegovina',
  'BW': 'Botswana',
  'BV': 'Bouvet Island',
  'BR': 'Brazil',
  'IO': 'British Indian Ocean Territory',
  'BN': 'Brunei',
  'BG': 'Bulgaria',
  'BF': 'Burkina Faso',
  'BI': 'Burundi',
  'KH': 'Cambodia',
  'CM': 'Cameroon',
  'CA': 'Canada',
  'CV': 'Cabo Verde',
  'KY': 'Cayman Islands',
  'CF': 'Central African Republic',
  'TD': 'Chad',
  'CL': 'Chile',
  'CN': 'China',
  'CX': 'Christmas Island',
  'CC': 'Cocos (Keeling) Islands',
  'CO': 'Colombia',
  'KM': 'Comoros',
  'CG': 'Congo',
  'CD': 'Democratic Republic of the Congo',
  'CK': 'Cook Islands',
  'CR': 'Costa Rica',
  'CI': "Côte d'Ivoire",
  'HR': 'Croatia',
  'CU': 'Cuba',
  'CW': 'Curaçao',
  'CY': 'Cyprus',
  'CZ': 'Czechia',
  'DK': 'Denmark',
  'DJ': 'Djibouti',
  'DM': 'Dominica',
  'DO': 'Dominican Republic',
  'EC': 'Ecuador',
  'EG': 'Egypt',
  'SV': 'El Salvador',
  'GQ': 'Equatorial Guinea',
  'ER': 'Eritrea',
  'EE': 'Estonia',
  'SZ': 'Eswatini',
  'ET': 'Ethiopia',
  'FK': 'Falkland Islands',
  'FO': 'Faroe Islands',
  'FJ': 'Fiji',
  'FI': 'Finland',
  'FR': 'France',
  'GF': 'French Guiana',
  'PF': 'French Polynesia',
  'TF': 'French Southern Territories',
  'GA': 'Gabon',
  'GM': 'Gambia',
  'GE': 'Georgia',
  'DE': 'Germany',
  'GH': 'Ghana',
  'GI': 'Gibraltar',
  'GR': 'Greece',
  'GL': 'Greenland',
  'GD': 'Grenada',
  'GP': 'Guadeloupe',
  'GU': 'Guam',
  'GT': 'Guatemala',
  'GG': 'Guernsey',
  'GN': 'Guinea',
  'GW': 'Guinea-Bissau',
  'GY': 'Guyana',
  'HT': 'Haiti',
  'HM': 'Heard Island and McDonald Islands',
  'VA': 'Holy See',
  'HN': 'Honduras',
  'HK': 'Hong Kong',
  'HU': 'Hungary',
  'IS': 'Iceland',
  'IN': 'India',
  'ID': 'Indonesia',
  'IR': 'Iran',
  'IQ': 'Iraq',
  'IE': 'Ireland',
  'IM': 'Isle of Man',
  'IL': 'Israel',
  'IT': 'Italy',
  'JM': 'Jamaica',
  'JP': 'Japan',
  'JE': 'Jersey',
  'JO': 'Jordan',
  'KZ': 'Kazakhstan',
  'KE': 'Kenya',
  'KI': 'Kiribati',
  'KP': 'North Korea',
  'KR': 'South Korea',
  'KW': 'Kuwait',
  'KG': 'Kyrgyzstan',
  'LA': 'Laos',
  'LV': 'Latvia',
  'LB': 'Lebanon',
  'LS': 'Lesotho',
  'LR': 'Liberia',
  'LY': 'Libya',
  'LI': 'Liechtenstein',
  'LT': 'Lithuania',
  'LU': 'Luxembourg',
  'MO': 'Macao',
  'MG': 'Madagascar',
  'MW': 'Malawi',
  'MY': 'Malaysia',
  'MV': 'Maldives',
  'ML': 'Mali',
  'MT': 'Malta',
  'MH': 'Marshall Islands',
  'MQ': 'Martinique',
  'MR': 'Mauritania',
  'MU': 'Mauritius',
  'YT': 'Mayotte',
  'MX': 'Mexico',
  'FM': 'Micronesia',
  'MD': 'Moldova',
  'MC': 'Monaco',
  'MN': 'Mongolia',
  'ME': 'Montenegro',
  'MS': 'Montserrat',
  'MA': 'Morocco',
  'MZ': 'Mozambique',
  'MM': 'Myanmar',
  'NA': 'Namibia',
  'NR': 'Nauru',
  'NP': 'Nepal',
  'NL': 'Netherlands',
  'NC': 'New Caledonia',
  'NZ': 'New Zealand',
  'NI': 'Nicaragua',
  'NE': 'Niger',
  'NG': 'Nigeria',
  'NU': 'Niue',
  'NF': 'Norfolk Island',
  'MK': 'North Macedonia',
  'MP': 'Northern Mariana Islands',
  'NO': 'Norway',
  'OM': 'Oman',
  'PK': 'Pakistan',
  'PW': 'Palau',
  'PS': 'Palestine',
  'PA': 'Panama',
  'PG': 'Papua New Guinea',
  'PY': 'Paraguay',
  'PE': 'Peru',
  'PH': 'Philippines',
  'PN': 'Pitcairn Islands',
  'PL': 'Poland',
  'PT': 'Portugal',
  'PR': 'Puerto Rico',
  'QA': 'Qatar',
  'RE': 'Réunion',
  'RO': 'Romania',
  'RU': 'Russia',
  'RW': 'Rwanda',
  'BL': 'Saint Barthélemy',
  'SH': 'Saint Helena, Ascension and Tristan da Cunha',
  'KN': 'Saint Kitts and Nevis',
  'LC': 'Saint Lucia',
  'MF': 'Saint Martin',
  'PM': 'Saint Pierre and Miquelon',
  'VC': 'Saint Vincent and the Grenadines',
  'WS': 'Samoa',
  'SM': 'San Marino',
  'ST': 'São Tomé and Príncipe',
  'SA': 'Saudi Arabia',
  'SN': 'Senegal',
  'RS': 'Serbia',
  'SC': 'Seychelles',
  'SL': 'Sierra Leone',
  'SG': 'Singapore',
  'SX': 'Sint Maarten',
  'SK': 'Slovakia',
  'SI': 'Slovenia',
  'SB': 'Solomon Islands',
  'SO': 'Somalia',
  'ZA': 'South Africa',
  'GS': 'South Georgia and the South Sandwich Islands',
  'SS': 'South Sudan',
  'ES': 'Spain',
  'LK': 'Sri Lanka',
  'SD': 'Sudan',
  'SR': 'Suriname',
  'SJ': 'Svalbard and Jan Mayen',
  'SE': 'Sweden',
  'CH': 'Switzerland',
  'SY': 'Syria',
  'TW': 'Taiwan',
  'TJ': 'Tajikistan',
  'TZ': 'Tanzania',
  'TH': 'Thailand',
  'TL': 'Timor-Leste',
  'TG': 'Togo',
  'TK': 'Tokelau',
  'TO': 'Tonga',
  'TT': 'Trinidad and Tobago',
  'TN': 'Tunisia',
  'TR': 'Turkey',
  'TM': 'Turkmenistan',
  'TC': 'Turks and Caicos Islands',
  'TV': 'Tuvalu',
  'UG': 'Uganda',
  'UA': 'Ukraine',
  'AE': 'United Arab Emirates',
  'GB': 'United Kingdom',
  'US': 'United States',
  'UM': 'U.S. Outlying Islands',
  'UY': 'Uruguay',
  'UZ': 'Uzbekistan',
  'VU': 'Vanuatu',
  'VE': 'Venezuela',
  'VN': 'Vietnam',
  'VG': 'British Virgin Islands',
  'VI': 'U.S. Virgin Islands',
  'WF': 'Wallis and Futuna',
  'EH': 'Western Sahara',
  'YE': 'Yemen',
  'ZM': 'Zambia',
  'ZW': 'Zimbabwe',
  // Widely used, non-ISO code:
  'XK': 'Kosovo',
};

  class UserGeolocation {
    constructor() {
      this.country = null;
      this.loading = false;
      this.loadPromise = null;
    }

    /**
     * Get cached country from localStorage
     * @returns {Object|null} Country object or null if expired/not found
     */
    _getCached() {
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (!cached) return null;

        const data = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache exists and is not expired
        if (data.timestamp && (now - data.timestamp) < CACHE_DURATION) {
          return data.country;
        }
      } catch (e) {
        console.warn('[UserGeo] Error reading from localStorage:', e);
      }
      return null;
    }

    /**
     * Save country to localStorage
     * @param {Object} country - Country object { code, name }
     */
    _setCached(country) {
      try {
        const data = {
          country: country,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn('[UserGeo] Error writing to localStorage:', e);
      }
    }

    /**
     * Fetch country from IP geolocation API
     * @returns {Promise<Object>} Country object { code, name }
     */
    async _fetchFromAPI() {
      try {
        const response = await fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        const countryCode = (data.country || DEFAULT_COUNTRY).toUpperCase();
        const country = {
          code: countryCode,
          name: COUNTRY_NAMES[countryCode] || countryCode,
          city: data.city || null,
          region: data.region || null,
          timezone: data.timezone || null,
        };
        
        return country;
      } catch (error) {
        console.warn('[UserGeo] API fetch failed:', error);
        // Return default country on error
        return {
          code: DEFAULT_COUNTRY,
          name: COUNTRY_NAMES[DEFAULT_COUNTRY],
          city: null,
          region: null,
          timezone: null,
        };
      }
    }

    /**
     * Get user's country (cached or fresh)
     * @param {boolean} forceRefresh - Force fetch from API even if cached
     * @returns {Promise<Object>} Country object { code, name }
     */
    async getCountry(forceRefresh = false) {
      // Return cached result if already loaded
      if (this.country && !forceRefresh) {
        return this.country;
      }

      // Return existing promise if already loading
      if (this.loading && this.loadPromise) {
        return this.loadPromise;
      }

      // Check localStorage cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = this._getCached();
        if (cached) {
          this.country = cached;
          return cached;
        }
      }

      // Fetch from API
      this.loading = true;
      this.loadPromise = this._fetchFromAPI()
        .then(country => {
          this.country = country;
          this._setCached(country);
          this.loading = false;
          return country;
        })
        .catch(error => {
          console.error('[UserGeo] Error getting country:', error);
          this.loading = false;
          // Return default on error
          const defaultCountry = {
            code: DEFAULT_COUNTRY,
            name: COUNTRY_NAMES[DEFAULT_COUNTRY],
          };
          this.country = defaultCountry;
          return defaultCountry;
        });

      return this.loadPromise;
    }

    /**
     * Get cached country synchronously (no API call)
     * Returns null if not yet loaded. Use this for immediate/synchronous access.
     * @returns {Object|null} Country object { code, name, ... } or null if not loaded
     */
    getCached() {
      return this.country;
    }

    /**
     * Get country code only
     * @returns {Promise<string>} Two-letter country code
     */
    async getCountryCode() {
      const country = await this.getCountry();
      return country.code;
    }

    /**
     * Get country name only
     * @returns {Promise<string>} Country name
     */
    async getCountryName() {
      const country = await this.getCountry();
      return country.name;
    }

    /**
     * Check if current country matches given code(s)
     * @param {string|Array<string>} codes - Country code(s) to check
     * @returns {Promise<boolean>}
     */
    async isCountry(codes) {
      const country = await this.getCountry();
      const codeArray = Array.isArray(codes) ? codes : [codes];
      return codeArray.some(code => code.toUpperCase() === country.code);
    }

    /**
     * Check if current country is US
     * @returns {Promise<boolean>}
     */
    async isUS() {
      return this.isCountry('US');
    }

    /**
     * Check if current country is NOT US
     * @returns {Promise<boolean>}
     */
    async isNonUS() {
      const isUS = await this.isUS();
      return !isUS;
    }

    /**
     * Clear cached country data
     */
    clearCache() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        this.country = null;
      } catch (e) {
        console.warn('[UserGeo] Error clearing cache:', e);
      }
    }

    /**
     * Get cache info for debugging
     * @returns {Object} Cache information
     */
    getCacheInfo() {
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (!cached) {
          return { hasCached: false };
        }

        const data = JSON.parse(cached);
        const now = Date.now();
        const age = data.timestamp ? now - data.timestamp : null;
        
        return {
          hasCached: true,
          cachedCountry: data.country || null,
          timestamp: data.timestamp,
          ageMs: age,
          ageHours: age ? (age / (1000 * 60 * 60)).toFixed(2) : null,
          isExpired: age ? age > CACHE_DURATION : null,
        };
      } catch (e) {
        return { error: e.message };
      }
    }
  }

  // Create and expose global instance
  window.UserGeo = new UserGeolocation();

  // Auto-initialize on load (non-blocking)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.UserGeo.getCountry().catch(() => {});
    });
  } else {
    // DOM already loaded
    window.UserGeo.getCountry().catch(() => {});
  }

})();
